export type ThemeMode = 'light' | 'dark';

export class ThemeModeHelper {
    static readonly storageKey = 'itp-theme-mode';
    static readonly authLightLoaderKey = 'itp-auth-light-loader';
    static readonly defaultMode: ThemeMode = 'dark';
    static readonly attributeName = 'data-itp-theme';

    static getCurrentMode(): ThemeMode {
        if (!ThemeModeHelper.canUseBrowserStorage()) {
            return ThemeModeHelper.defaultMode;
        }

        try {
            return ThemeModeHelper.normalizeMode(localStorage.getItem(ThemeModeHelper.storageKey));
        } catch {
            return ThemeModeHelper.defaultMode;
        }
    }

    static setMode(mode: ThemeMode): ThemeMode {
        const normalizedMode = ThemeModeHelper.normalizeMode(mode);

        ThemeModeHelper.persistMode(normalizedMode);

        return ThemeModeHelper.applyThemeMode(normalizedMode);
    }

    static persistMode(mode: ThemeMode): ThemeMode {
        const normalizedMode = ThemeModeHelper.normalizeMode(mode);

        try {
            localStorage.setItem(ThemeModeHelper.storageKey, normalizedMode);
        } catch {
            // Theme switching should still work in restricted storage contexts.
        }

        return normalizedMode;
    }

    static toggleMode(): ThemeMode {
        return ThemeModeHelper.setMode(ThemeModeHelper.getCurrentMode() === 'dark' ? 'light' : 'dark');
    }

    static applyThemeMode(mode: ThemeMode = ThemeModeHelper.getCurrentMode()): ThemeMode {
        // Keep the post-login splash in Light Mode until the app finishes booting.
        if (ThemeModeHelper.hasAuthLightLoaderOverride()) {
            mode = 'light';
        }

        const normalizedMode = ThemeModeHelper.normalizeMode(mode);

        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute(ThemeModeHelper.attributeName, normalizedMode);
            document.documentElement.style.colorScheme = normalizedMode;
        }

        return normalizedMode;
    }

    /** Auth pages always present Light Mode branding (matches the Login page design). */
    static applyThemeModeForLogin(): ThemeMode {
        if (typeof document !== 'undefined') {
            document.documentElement.setAttribute(ThemeModeHelper.attributeName, 'light');
            document.documentElement.style.colorScheme = 'light';
        }

        return 'light';
    }

    /** Mark the next full-page navigation so the root splash uses Light Mode like Login. */
    static prepareLightLoaderForNextNavigation(): void {
        try {
            sessionStorage.setItem(ThemeModeHelper.authLightLoaderKey, '1');
        } catch {
            // Ignore restricted storage contexts.
        }
    }

    static hasAuthLightLoaderOverride(): boolean {
        try {
            return sessionStorage.getItem(ThemeModeHelper.authLightLoaderKey) === '1';
        } catch {
            return false;
        }
    }

    /** Clear the one-shot Light Mode splash override and restore the user's theme. */
    static clearAuthLightLoaderOverride(): ThemeMode {
        try {
            sessionStorage.removeItem(ThemeModeHelper.authLightLoaderKey);
        } catch {
            // Ignore restricted storage contexts.
        }

        return ThemeModeHelper.applyThemeMode();
    }

    private static normalizeMode(mode: string): ThemeMode {
        return mode === 'light' ? 'light' : ThemeModeHelper.defaultMode;
    }

    private static canUseBrowserStorage(): boolean {
        try {
            return typeof localStorage !== 'undefined';
        } catch {
            return false;
        }
    }
}
